import { AR_RECOVERY_CANONICAL_COMPANY_ID } from "./arRecoveryWorkbenchApi";

export type ARClientPortalAccount = {
  accountId: string;
  clientId: string;
  customerId: string;
  customerName: string;
  customerNumber: string;
  billingEmail: string;
  accountNumber: string;
  status: string;
  placementStatus: string;
  placementId: string | null;
  placementNumber: string | null;
  originalBalanceCents: number;
  currentBalanceCents: number;
  currentRecoveryBalanceCents: number;
  currency: string;
  version: number;
  updatedAt: string;
};

export type ARClientPortalPayload = {
  source: "canonical-ar-client-portal-d1";
  companyId: string;
  membership: {
    roleKey: string;
    permissionProfile: string;
    permissions: {
      viewDashboard: boolean;
      viewAccounts: boolean;
      viewStatements: boolean;
      viewDocuments: boolean;
      viewTimeline: boolean;
      reportPayment: boolean;
      uploadPaymentFile: boolean;
      manageUsers: boolean;
      readOnly: boolean;
    };
  };
  clients: Array<{
    clientId: string;
    clientNumber: string;
    displayName: string;
    status: string;
    currency: string;
    paymentReportingEnabled: boolean;
    statementServicesEnabled: boolean;
    recoveryServicesEnabled: boolean;
    version: number;
    updatedAt: string;
  }>;
  accounts: ARClientPortalAccount[];
  invoices: Array<{
    invoiceId: string;
    clientId: string;
    customerId: string;
    accountId: string;
    placementId: string | null;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    originalAmountCents: number;
    currentBalanceCents: number;
    status: string;
    currency: string;
    version: number;
  }>;
  statements: Array<{
    statementId: string;
    clientId: string | null;
    customerId: string;
    accountId: string;
    statementNumber: string;
    statementDate: string;
    asOfDate: string;
    balanceCents: number;
    openInvoiceCount: number;
    recipientEmail: "configured" | "not_configured";
    deliveryStatus: string;
    lifecycleStatus: string;
    downloadedStatus: string;
    customerName: string;
    customerNumber: string;
    version: number;
    updatedAt: string;
  }>;
  documents: Array<{
    documentVersionId: string;
    statementId: string;
    statementNumber: string;
    accountId: string;
    customerName: string;
    versionNumber: number;
    status: string;
    mimeType: string;
    fileSize: number;
    available: boolean;
    createdAt: string;
  }>;
  settlementBilling: {
    invoices: Array<{
      invoiceDraftId: string;
      settlementId: string;
      settlementNumber: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate: string;
      paymentTerms: string;
      amountDueCents: number;
      currency: string;
      invoiceStatus: string;
      paymentStatus: string;
      delinquencyStatus: string;
      clarificationStatus: string;
      downloadable: boolean;
      updatedAt: string;
    }>;
    statements: Array<{
      statementVersionId: string;
      settlementId: string;
      settlementNumber: string;
      statementNumber: string;
      version: number;
      sourceSettlementVersion: number;
      status: string;
      current: boolean;
      mimeType: string;
      fileName: string;
      generatedAt: string;
      downloadable: boolean;
    }>;
  };
  billingCenter: {
    invoices: Array<{
      billingInvoiceId: string;
      clientId: string;
      settlementId: string;
      settlementNumber: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate: string;
      paymentTerms: string;
      originalTotalCents: number;
      amountPaidCents: number;
      remainingBalanceCents: number;
      currency: string;
      invoiceStatus: string;
      paymentStatus: string;
      stripeStatus: string;
      payNowAvailable: boolean;
      payNowUrl: string | null;
      downloadable: boolean;
      updatedAt: string;
    }>;
    payments: Array<{
      paymentId: string;
      billingInvoiceId: string;
      invoiceNumber: string;
      paymentStatus: string;
      amountCents: number;
      currency: string;
      paymentMethodSummary: string;
      providerReferenceStatus: "configured" | "missing";
      occurredAt: string;
    }>;
    receipts: Array<{
      receiptId: string;
      billingInvoiceId: string;
      invoiceNumber: string;
      receiptNumber: string;
      amountPaidCents: number;
      remainingBalanceCents: number;
      currency: string;
      paymentMethodSummary: string;
      paymentDate: string;
      issuedAt: string;
      downloadable: boolean;
    }>;
    history: Array<{
      historyId: string;
      billingInvoiceId: string;
      invoiceNumber: string;
      eventType: string;
      title: string;
      summary: string;
      occurredAt: string;
    }>;
    experience: {
      dashboard: {
        currentBalanceCents: number;
        outstandingBalanceCents: number;
        invoicesDue: number;
        upcomingAutoPay: string;
        nextScheduledAutoPay: string | null;
        nextDueDate: string | null;
        recentPayments: Array<{
          paymentId: string;
          invoiceNumber: string;
          amountCents: number;
          status: string;
          methodSummary: string;
          occurredAt: string;
        }>;
        billingContact: {
          primary: string;
          accountsPayable: string;
          escalation: string;
          executive: string;
        };
        accountStanding: string;
        serviceHoldWarning: string;
      };
      policy: {
        policyName: string;
        templateName: string;
        triggerRules: string[];
        feeModel: Record<string, unknown>;
        paymentTerms: Record<string, unknown>;
        autopayRules: Record<string, unknown>;
        serviceHoldRules: Record<string, unknown>;
        riskRules: Record<string, unknown>;
        reminderRules: string[];
        invoiceApprovalRules: Record<string, unknown>;
        billingContacts: Record<string, unknown>;
      } | null;
      riskProfile: {
        riskLevel: string;
        paymentReliabilityScore: number;
        latePaymentCount: number;
        returnedPaymentCount: number;
        disputeCount: number;
        autopayFailureCount: number;
        serviceHoldStatus: string;
        approvalLevel: string;
      } | null;
      preferences: {
        invoiceDelivery: string;
        reminderPreferences: string;
        savedPaymentMethods: string;
        autopayPreferences: string;
        policyControlled: boolean;
        autopayConsent: {
          consentStatus: string;
          policyBasis: string;
          clientElection: string;
          requiredByRisk: boolean;
          timingRule: string;
          paymentMethodSummary: string;
          cancellationRestricted: boolean;
        } | null;
        availableClientActions: string[];
      };
      serviceHolds: Array<{
        holdStatus: string;
        holdScope: string;
        effectiveDate: string | null;
        clientNoticeStatus: string;
        reinstatementCriteria: string;
      }>;
      reminderSchedules: Array<{
        reminderType: string;
        scheduledDate: string;
        channel: string;
        status: string;
        clientSafeTemplate: string;
      }>;
      analytics: {
        invoiceCount: number;
        paidInvoiceCount: number;
        receiptCount: number;
        averagePaymentTime: string;
      };
    } | null;
  };
  payments: Array<{
    paymentId: string;
    clientId: string;
    customerId: string | null;
    accountId: string | null;
    customerName: string;
    accountNumber: string;
    paymentSource: string;
    paymentReference: string;
    referenceStatus: "configured" | "missing";
    paymentDate: string;
    reportedDate: string;
    amountCents: number;
    currency: string;
    paymentMethod: string;
    verificationStatus: string;
    allocationStatus: string;
    operationalStatus: string;
    duplicateStatus: string;
    exceptionStatus: string;
    version: number;
    updatedAt: string;
  }>;
  paymentUploads: Array<{
    uploadId: string;
    clientId: string;
    filename: string;
    fileType: string;
    fileSize: number;
    rowCount: number;
    processingStatus: string;
    validRowCount: number;
    invalidRowCount: number;
    duplicateRowCount: number;
    matchedRowCount: number;
    exceptionRowCount: number;
    uploadedAt: string;
    completedAt: string | null;
  }>;
  timeline: ARClientPortalEvent[];
  activity: ARClientPortalEvent[];
  notifications: Array<{ id: string; type: string; title: string; detail: string }>;
  pagination: {
    accounts: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
    defaultPageSize: number;
  };
  generatedAt: string;
};

export type ARClientPortalEvent = {
  eventId: string;
  sourceEventId?: string;
  correlationId?: string;
  clientId: string;
  customerId: string;
  accountId: string;
  kind: "timeline" | "activity";
  eventType: string;
  category: string;
  title: string;
  summary: string;
  occurredAt: string;
};

export type ARClientPortalQuery = {
  page?: number;
  pageSize?: 25 | 50 | 100;
  accountId?: string;
  accountSearch?: string;
  accountStatus?: string;
  accountSort?: "customer" | "balance";
};

export type ARClientPlacementSubmission = {
  submissionId: string;
  clientId: string;
  submissionMethod: "MANUAL" | "CSV" | "XLSX";
  filename: string | null;
  status: string;
  totalRows: number;
  acceptedRows: number;
  rejectedRows: number;
  duplicateRows: number;
  warningRows: number;
  submittedAt: string;
  completedAt: string | null;
};

export type ARClientPlacementResponse = { submission: ARClientPlacementSubmission; replay: boolean };

export async function createClientAccountPlacement(input: {
  clientId?: string;
  idempotencyKey: string;
  customerNumber?: string;
  customerName?: string;
  accountNumber?: string;
  originalBalance?: string;
  currentBalance?: string;
  currency?: string;
  placementDate?: string;
  externalReference?: string;
  file?: File;
}) {
  const form = new FormData();
  for (const [key, value] of Object.entries(input)) if (value !== undefined && value !== "") form.append(key, value instanceof File ? value : String(value));
  const response = await fetch("/api/ar-recovery/client-portal/account-placement", { method: "POST", body: form, credentials: "same-origin" });
  if (!response.ok) throw new Error((await response.text()).trim() || `Account placement failed (${response.status}).`);
  return response.json() as Promise<ARClientPlacementResponse>;
}

export type ARClientBillingDomainPayload = {
  source: "canonical-ar-client-billing-d1";
  companyId: string;
  clientId: string;
  resource: "dashboard" | "invoices" | "invoice" | "payments" | "receipts" | "history" | "settings";
  invoices: ARClientPortalPayload["billingCenter"]["invoices"];
  payments: ARClientPortalPayload["billingCenter"]["payments"];
  receipts: ARClientPortalPayload["billingCenter"]["receipts"];
  history: ARClientPortalPayload["billingCenter"]["history"];
  settings: ARClientPortalPayload["billingCenter"]["experience"] | null;
  experience: ARClientPortalPayload["billingCenter"]["experience"] | null;
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
};

export type ARClientBillingDashboard = {
  source: "canonical-ar-client-billing-dashboard-d1";
  companyId: string;
  clientId: string;
  resource: "dashboard";
  summary: { totalOutstandingBalance: number; unpaidInvoiceCount: number; overdueInvoiceCount: number; dueSoonInvoiceCount: number; currentStanding: string };
  recentPayments: ARClientPortalPayload["billingCenter"]["payments"];
  recentReceipts: ARClientPortalPayload["billingCenter"]["receipts"];
  recentActivity: ARClientPortalPayload["billingCenter"]["history"];
};

export type ARClientBillingDomainQuery = {
  resource: ARClientBillingDomainPayload["resource"];
  invoiceId?: string;
  page?: number;
  pageSize?: 25 | 50 | 100;
  search?: string;
  invoiceStatus?: string;
  sort?: "due" | "updated";
};

export async function getCanonicalARClientPortal(companyId = AR_RECOVERY_CANONICAL_COMPANY_ID, options: ARClientPortalQuery = {}) {
  const query = new URLSearchParams({ companyId });
  for (const [key, value] of Object.entries(options)) {
    if (value != null && value !== "") query.set(key, String(value));
  }
  const response = await fetch(`/api/ar-recovery/client-portal?${query}`, { credentials: "same-origin" });
  if (!response.ok) {
    const message = (await response.text()).trim() || `Client Portal request failed (${response.status}).`;
    throw new Error(message);
  }
  return response.json() as Promise<ARClientPortalPayload>;
}

export async function getClientBillingDomain(options: ARClientBillingDomainQuery, companyId = AR_RECOVERY_CANONICAL_COMPANY_ID) {
  const query = new URLSearchParams({ companyId });
  for (const [key, value] of Object.entries(options)) {
    if (value != null && value !== "") query.set(key, String(value));
  }
  const response = await fetch(`/api/ar-recovery/client-portal/billing?${query}`, { credentials: "same-origin" });
  if (!response.ok) {
    const message = (await response.text()).trim() || `Client Billing request failed (${response.status}).`;
    throw new Error(message);
  }
  return response.json() as Promise<ARClientBillingDomainPayload>;
}

export async function getClientBillingDashboard(companyId = AR_RECOVERY_CANONICAL_COMPANY_ID) {
  const query = new URLSearchParams({ companyId, resource: "dashboard" });
  const response = await fetch(`/api/ar-recovery/client-portal/billing?${query}`, { credentials: "same-origin" });
  if (!response.ok) {
    const message = (await response.text()).trim() || `Client Billing Dashboard request failed (${response.status}).`;
    throw new Error(message);
  }
  return response.json() as Promise<ARClientBillingDashboard>;
}
